import { useRestQuery } from '@/lib/hook/tanstackQuery'
import { DisputeAttachment } from '@/lib/hook/useUploadDisputeFiles'
import { ApiQueryEndpoint } from '@/src/ApiQueryEndpoint'

type RmaProduct = {
  item_id: number
  product_id: number
  amount: number
  // Quantite commandee (borne max pour l'edition de la quantite en litige).
  order_amount: number
  // Quantite deja livree/expediee.
  shipped_amount: number
  reason: string
  product: string
  price?: string | number
}

// Resolutions possibles renvoyees par le backend (entite PharmeRma).
// 'invoice_provided' est la seule resolution d'une demande de facture (#463).
export type DisputeResolution = 'resend' | 'promo' | 'refund_stripe' | 'invoice_provided' | 'none'

// Motif du litige expose par le backend (DisputeManager::getReasonCodeMap).
// 'invoice_request' est porte par l'entete du litige : ces litiges n'ont aucune
// ligne produit, leur motif ne peut donc pas etre lu sur `products`.
export type DisputeReasonKey = 'missing_items' | 'damaged' | 'wrong_product' | 'invoice_request'

const DISPUTE_REASON_KEYS: DisputeReasonKey[] = [
  'missing_items',
  'damaged',
  'wrong_product',
  'invoice_request',
]

const toDisputeReasonKey = (value: unknown): DisputeReasonKey | undefined =>
  DISPUTE_REASON_KEYS.includes(value as DisputeReasonKey) ? (value as DisputeReasonKey) : undefined

type Rma = {
  rma_id: number
  order_id: number
  status: string
  date: string
  products: RmaProduct[]
  // Motif porte par le litige lui-meme (litige sans produit, ex: demande de
  // facture = 3). Absent pour les motifs portes par les lignes produits.
  reason?: number
  // Type de demande normalise par le backend (liste et detail) : couvre aussi
  // les litiges sans ligne produit.
  dispute_reason_key?: DisputeReasonKey
  // Champs de resolution exposes par GET rma/:id (detail uniquement).
  allowed_resolutions?: DisputeResolution[]
  can_reopen?: boolean
  replaceable?: boolean
  closed_at?: number
  resolved_at?: number
  resolution_type?: DisputeResolution | null
  // Code de reduction (avoir) si resolution promo — expose detail + liste.
  coupon_code?: string | null
  // Acteur attendu + date d'auto-cloture (expose par PharmeRma, detail + liste).
  responsible_actor?: 'vendor' | 'admin' | 'customer' | 'none'
  auto_close_at?: number
  // Pieces jointes du litige (detail uniquement) — les PJ des messages sont
  // portees par les messages (GET communication/:order_id).
  attachments?: DisputeAttachment[]
}

type RmasResult = {
  params: {
    total_items: number
    page?: number
    items_per_page?: number
    rma_id?: number
    rma_ids?: number[]
    order_ids?: number[]
    status?: string
    action?: number
  }
  rmas: Rma[]
}

type UseRmasArgs = {
  rma_id?: number
  rma_ids?: number[]
  order_ids?: number[]
  status?: string
  items_per_page?: number
  page?: number
  action?: number
  enabled?: boolean
}

/**
 * Reproduit fidelement le resolver getRmas (apollo/server/resolvers/Rma/getRmas.ts).
 * Construit l'URL CS-Cart `rma` ou `rma/:rma_id` avec les memes query params,
 * puis remappe le resultat dans la meme forme que l'ancien resolver GraphQL.
 */
const useRmas = (args: UseRmasArgs = {}) => {
  const { rma_id, rma_ids, order_ids, status, items_per_page, page, action, enabled } = args

  let apiUrl = ApiQueryEndpoint.RMA.GET_RMAS.endpoint

  if (rma_id) {
    apiUrl += `/${rma_id}`
  } else {
    const queryParams: string[] = []
    if (rma_ids && rma_ids.length > 0) {
      queryParams.push(`rma_ids=${rma_ids.join(',')}`)
    }
    if (order_ids && order_ids.length > 0) {
      queryParams.push(`order_ids=${order_ids.join(',')}`)
    }
    if (status) {
      queryParams.push(`status=${status}`)
    }
    if (items_per_page) {
      queryParams.push(`items_per_page=${items_per_page}`)
    }
    if (page) {
      queryParams.push(`page=${page}`)
    }
    if (action) {
      queryParams.push(`action=${action}`)
    }
    if (queryParams.length > 0) {
      apiUrl += `?${queryParams.join('&')}`
    }
  }

  return useRestQuery<any, Error, RmasResult>({
    endpoint: apiUrl,
    queryKey: ['rma', rma_id, rma_ids, order_ids, status, items_per_page, page, action],
    enabled,
    select: (result: any): RmasResult => {
      if (!result || !result.rmas || result.rmas.length === 0) {
        return {
          params: {
            total_items: 0,
            page,
            items_per_page,
            rma_id,
            rma_ids,
            order_ids,
            status,
            action,
          },
          rmas: [],
        }
      }

      return {
        params: {
          ...result.params,
          rma_id,
          rma_ids,
          order_ids,
          status,
          action,
        },
        rmas: result.rmas.map((rma: any) => ({
          // CS-Cart renvoie les identifiants numeriques sous forme de chaine dans le JSON.
          // Le type Rma declare rma_id: number et les mutations (useUpdateDispute, useDisputeAction,
          // useDisputeUpdateAmount) valident rma_id via z.number() : on force donc la coercion ici,
          // a la source, pour garantir un vrai number a tous les consommateurs.
          rma_id: Number(rma.rma_id),
          order_id: rma.order_id,
          status: rma.status,
          date: rma.date,
          reason: typeof rma.reason === 'number' ? rma.reason : undefined,
          dispute_reason_key: toDisputeReasonKey(rma.dispute_reason_key),
          products: Array.isArray(rma.products)
            ? rma.products.map((product: any) => ({
                item_id: product.item_id,
                product_id: product.product_id,
                amount: product.amount,
                order_amount: product.order_amount,
                shipped_amount: product.shipped_amount,
                reason: product.reason,
                product: product.product,
                price: product.price,
              }))
            : [],
          // Champs de resolution (presents uniquement sur GET rma/:id).
          allowed_resolutions: Array.isArray(rma.allowed_resolutions)
            ? rma.allowed_resolutions
            : undefined,
          can_reopen: typeof rma.can_reopen === 'boolean' ? rma.can_reopen : undefined,
          replaceable: typeof rma.replaceable === 'boolean' ? rma.replaceable : undefined,
          closed_at: typeof rma.closed_at === 'number' ? rma.closed_at : undefined,
          resolved_at: typeof rma.resolved_at === 'number' ? rma.resolved_at : undefined,
          resolution_type: rma.resolution_type ?? undefined,
          coupon_code: rma.coupon_code ?? undefined,
          responsible_actor: rma.responsible_actor ?? undefined,
          auto_close_at: typeof rma.auto_close_at === 'number' ? rma.auto_close_at : undefined,
          attachments: Array.isArray(rma.attachments) ? rma.attachments : undefined,
        })),
      }
    },
  })
}

export default useRmas
